import { resolveBreakpoint, resolveNodeForBreakpoint } from "../contract/breakpoints.js";

export interface BreakpointSpecLite {
  name?: string;
  min_width?: number;
  max_width?: number;
}

export interface BreakpointVisibilitySuggestion {
  name: string;
  viewportPx: number;
}

export interface BreakpointVisibilityHint {
  currentBreakpoint: string | null;
  currentVisible: boolean;
  suggestions: BreakpointVisibilitySuggestion[];
}

export function listVisibleBreakpointNames(
  node: unknown,
  breakpoints: BreakpointSpecLite[],
): string[] {
  if (!node || typeof node !== "object") return [];
  const names = Array.isArray(breakpoints)
    ? breakpoints.map((bp) => bp?.name).filter((name): name is string => Boolean(name))
    : ["wide", "regular", "compact", "mobile"];
  return names.filter(
    (name) => resolveNodeForBreakpoint(node as Record<string, unknown>, name).visible,
  );
}

/** Width that exists in the viewer viewport select (or close). */
export function representativeViewportForBreakpoint(
  breakpoints: BreakpointSpecLite[],
  name: string,
): number {
  const bp = breakpoints.find((b) => b.name === name);
  if (!bp) return 1440;
  if (typeof bp.min_width === "number" && Number.isFinite(bp.min_width)) {
    return bp.min_width;
  }
  if (typeof bp.max_width === "number" && Number.isFinite(bp.max_width)) {
    return Math.min(479, Math.max(320, bp.max_width - 1));
  }
  return 390;
}

export function buildBreakpointVisibilityHint(
  node: unknown,
  breakpoints: BreakpointSpecLite[],
  viewportWidth: number | null | undefined,
): BreakpointVisibilityHint | null {
  if (!node || typeof node !== "object") return null;
  const width = viewportWidth != null && Number.isFinite(viewportWidth) ? viewportWidth : null;
  const currentBreakpoint = width != null ? resolveBreakpoint(breakpoints, width) : null;
  const currentVisible = resolveNodeForBreakpoint(
    node as Record<string, unknown>,
    currentBreakpoint,
  ).visible;
  const visibleNames = listVisibleBreakpointNames(node, breakpoints);
  const suggestions = visibleNames.map((name) => ({
    name,
    viewportPx: representativeViewportForBreakpoint(breakpoints, name),
  }));
  return { currentBreakpoint, currentVisible, suggestions };
}
